import { forwardRef } from 'react';
import type { ReactNode, Ref, PropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';

import type {
  HeadingTag,
  BaseProps,
  ForwardRefForwardPropsComponent,
  AsProp,
  PropsWithDefaults
} from '../../types';
import { defaultThemeProp } from '../../theme';
import { tryCatch } from '../../utils';
import { calculateFontSize } from '../../styles';
import type { FontSize } from '../../styles';

export interface TextProps extends BaseProps, AsProp {
  /** Text content. */
  children: ReactNode;
  /**
   * Typographical sizes and styles.
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary' | HeadingTag;
  /** Render text with semantic color based on a status. */
  status?: 'error' | 'warning' | 'success';
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLSpanElement | HTMLHeadingElement>;
}

type TextPropsWithDefaults = PropsWithDefaults<TextProps, 'variant'>;

export const StyledText = styled.span<TextPropsWithDefaults>(props => {
  const {
    variant,
    status,
    theme: {
      base: {
        'font-size': fontSize,
        'font-scale': fontScale,
        'font-family': fontFamily,
        palette: { 'foreground-color': foregroundColor, urgent: error, warn: warning, success },
        transparency: { 'transparent-2': secondaryAlpha }
      },
      components: { text }
    }
  } = props;

  let color: string | undefined;

  if (status) {
    color = {
      error,
      warning,
      success
    }[status];
  }

  if (variant === 'secondary') {
    color = tryCatch(() => rgba(color ?? foregroundColor, secondaryAlpha));
  }

  const fontSizes = calculateFontSize(fontSize, fontScale);

  return css`
    font-size: ${fontSizes[text[variant]['font-size'] as FontSize]};
    font-weight: ${text[variant]['font-weight']};
    font-family: ${text[variant]['font-family'] || fontFamily};
    color: ${color};
  `;
});

StyledText.defaultProps = defaultThemeProp;

const Text: ForwardRefForwardPropsComponent<TextProps> = forwardRef(function Text(
  { variant = 'primary', as, ...restProps }: PropsWithoutRef<TextProps>,
  ref: TextProps['ref']
) {
  // If variant is for a heading, and no as is passed, set as to heading tag.
  if (!as && /h\d/i.test(variant)) as = variant as HeadingTag;
  return <StyledText ref={ref} variant={variant} as={as} {...restProps} />;
});

export default Text;
